import { Link, useLocation } from 'react-router-dom'
import { useState } from 'react'
import { Menu, X } from 'lucide-react'

import { BrandLogo } from '@/components/brand/brand-logo'
import { siteNavigation } from '@/data/marketing'
import { authClient } from '@/lib/auth/client'
import { useHydrated } from '@/lib/use-hydrated'
import { cn } from '@/lib/utils'

import { Button } from '@/components/ui/button'

export function SiteHeader() {
  const pathname = useLocation().pathname
  const [mobileOpen, setMobileOpen] = useState(false)
  const { data: session } = authClient.useSession()
  // Only reflect auth state after hydration: the prerendered HTML is always the
  // logged-out header, so showing the logged-in variant on the first client
  // render would be a hydration mismatch (server HTML != client) — which can
  // blank the page. After hydration this updates to the real state.
  const hydrated = useHydrated()
  const isAuthenticated = hydrated && Boolean(session?.session)

  return (
    <header className="sticky top-0 z-30 border-b border-line/70 bg-white/90 backdrop-blur-xl">
      <div className="marketing-shell">
        <div>
          <div className="flex h-16 items-center justify-between gap-4">
            <Link to="/" aria-label="KindLumen home" onClick={() => setMobileOpen(false)}>
              <BrandLogo />
            </Link>

            <nav className="hidden items-center gap-1 lg:flex">
              {siteNavigation.map((item) => {
                const active = pathname === item.href

                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    className={cn(
                      'rounded-xl px-3 py-2 text-sm font-medium transition',
                      active
                        ? 'bg-brand/10 text-brand'
                        : 'text-ink-soft hover:bg-surface-strong hover:text-ink',
                    )}
                  >
                    {item.label}
                  </Link>
                )
              })}
            </nav>

            <div className="flex items-center gap-2 sm:gap-3">
              {isAuthenticated ? (
                <Button
                  asChild
                  size="sm"
                  className="hidden shadow-[0_12px_28px_rgba(37,99,235,0.18)] sm:inline-flex"
                >
                  <Link to="/dashboard">Dashboard</Link>
                </Button>
              ) : (
                <>
                  <Button asChild variant="ghost" size="sm" className="hidden md:inline-flex">
                    <Link to="/login">Log in</Link>
                  </Button>
                  <Button
                    asChild
                    size="sm"
                    className="hidden shadow-[0_12px_28px_rgba(37,99,235,0.18)] sm:inline-flex"
                  >
                    <Link to="/signup">Get started</Link>
                  </Button>
                </>
              )}
              <button
                type="button"
                aria-expanded={mobileOpen}
                aria-controls="marketing-mobile-nav"
                aria-label={mobileOpen ? 'Close navigation' : 'Open navigation'}
                onClick={() => setMobileOpen((open) => !open)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-line bg-white text-ink transition hover:border-brand/20 hover:text-brand lg:hidden"
              >
                {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>

          {mobileOpen ? (
            <div id="marketing-mobile-nav" className="border-t border-line/80 pb-4 pt-3 lg:hidden">
              <nav className="grid gap-2" aria-label="Mobile site navigation">
                {siteNavigation.map((item) => {
                  const active = pathname === item.href

                  return (
                    <Link
                      key={item.href}
                      to={item.href}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        'rounded-[18px] px-4 py-3 text-sm font-medium transition',
                        active
                          ? 'bg-brand text-white'
                          : 'bg-surface-strong text-ink-soft hover:bg-brand/5 hover:text-ink',
                      )}
                    >
                      {item.label}
                    </Link>
                  )
                })}
              </nav>

              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {isAuthenticated ? (
                  <Button asChild className="w-full sm:col-span-2">
                    <Link to="/dashboard" onClick={() => setMobileOpen(false)}>
                      Dashboard
                    </Link>
                  </Button>
                ) : (
                  <>
                    <Button asChild variant="secondary" className="w-full">
                      <Link to="/login" onClick={() => setMobileOpen(false)}>
                        Log in
                      </Link>
                    </Button>
                    <Button asChild className="w-full">
                      <Link to="/signup" onClick={() => setMobileOpen(false)}>
                        Get started
                      </Link>
                    </Button>
                  </>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </header>
  )
}
